import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle } from 'lucide-react';
import { projects, thesisInfo } from '../mock';
import { Button } from '../components/ui/button';

const Home = () => {
  const featuredProjects = projects.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center bg-charcoal text-off-white px-6 py-20">
        <div className="max-w-6xl w-full">
          <div className="space-y-6">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
              Asia B. Jewell
            </h1>
            <p className="text-xl md:text-2xl text-warm-grey max-w-3xl">
              Interdisciplinary artist exploring ADOS identity, Wake Work, and ancestral memory through performance, video, and material practice.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/projects">
                <Button className="bg-copper hover:bg-copper-dark text-charcoal font-semibold">
                  View Projects
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/thesis">
                <Button variant="outline" className="border-copper text-copper hover:bg-copper hover:text-charcoal">
                  Echoes of the Ninth
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Thesis Feature Section */}
      <section className="py-20 px-6 bg-off-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm uppercase tracking-wider text-burgundy font-semibold">Senior Thesis • May 2026</span>
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mt-4 mb-6">
                {thesisInfo.title}
              </h2>
              <p className="text-lg text-neutral-700 mb-6">
                {thesisInfo.description}
              </p>
              <Link to="/thesis">
                <Button className="bg-charcoal hover:bg-charcoal/90 text-off-white">
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative aspect-square bg-warm-grey rounded-lg overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_art-study-space/artifacts/vi79ncbd_MamaAnna.jpg"
                alt="Mama Anna - Echoes of the Ninth"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 px-6 bg-charcoal text-off-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Selected Works</h2>
              <p className="text-warm-grey text-lg">Recent projects exploring identity, memory, and material practice</p>
            </div>
            <Link to="/projects">
              <Button variant="outline" className="border-off-white text-off-white hover:bg-off-white hover:text-charcoal hidden md:flex">
                View All Projects
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {featuredProjects.map((project) => (
              <Link to={`/projects/${project.id}`} key={project.id}>
                <div className="group cursor-pointer">
                  <div className="relative aspect-square mb-4 overflow-hidden rounded-lg bg-off-white">
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                    {project.videoUrl && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                        <PlayCircle className="h-16 w-16 text-off-white" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-copper transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-warm-grey">{project.year} • {project.medium}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 md:hidden">
            <Link to="/projects" className="block">
              <Button variant="outline" className="w-full border-off-white text-off-white hover:bg-off-white hover:text-charcoal">
                View All Projects
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 px-6 bg-off-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-6">
            Research-Led Interdisciplinary Practice
          </h2>
          <p className="text-lg text-neutral-700 mb-8 leading-relaxed">
            As an American Descendant of Slavery (ADOS) Woman, I engage with Wake Work and epigenetic concepts to explore identity, ancestry, and blood memory. My practice spans performance, video, and interdisciplinary material work, to reclaim autonomy over Black bodies and their history.
          </p>
          <Link to="/about">
            <Button className="bg-charcoal hover:bg-charcoal/90 text-off-white">
              Read More About My Practice
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;